import React, { useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Patient from "./components/Patient";
import Clinician from "./components/Clinician";
import Home from "./components/Home";
import Login from "./components/Login";
import Register from "./components/Register";
import { PaymentSuccess, PaymentCancelled } from "./components/PaymentOutcome";
import LabReports from "./components/LabReports";
import MySummaries from "./components/MySummaries"; // P7
import MyResults from "./components/MyResults"; // "My Results" ecosystem plan, Phase 1
import MyPatientsResults from "./components/MyPatientsResults"; // "My Results" ecosystem plan, Phase 2
import MyPatientsSummaries from "./components/MyPatientsSummaries"; // 2026-10-22 — split out from MyPatientsResults, clinician counterpart of MySummaries
import MonthlyDigest from "./components/MonthlyDigest"; // P11c
import MyPayments from "./components/MyPayments";
import OrderTest from "./components/OrderTest";
import MyPatients from "./components/MyPatients";
import Settings from "./components/Settings";
import ChangePassword from "./components/ChangePassword";
import CompleteProfile from "./components/Complete_Profile"; // 2026-10-23 — clinician invite-only build, Piece 4
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import PatientConsent from "./components/PatientConsent";
import TermsOfUse from "./components/TermsOfUse";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { UserProvider } from "./components/UserContext";
import UserContext from "./components/UserContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminLogin from "./components/AdminLogin";
import AdminDashboard from "./components/AdminDashboard";
import AdminClinicians from "./components/AdminClinicians";
import AdminSalesReps from "./components/AdminSalesReps"; // 2026-10-23 — clinician invite-only build, Piece 6
import SalesRepDashboard from "./components/SalesRepDashboard"; // 2026-10-23 — clinician invite-only build, Piece 6
import AdminProtectedRoute from "./components/AdminProtectedRoute";

// After login, redirect based on role
function RoleHome() {
  const { user } = useContext(UserContext);
  if (!user) return <Navigate to="/" replace />;
  // 2026-10-23 addition (clinician invite-only build, Piece 6) — Home.js
  // is a patient/clinician AI-assistant card grid; a sales rep has no
  // business there (per the bug Home.js:579's own isClinician-only
  // branch would otherwise fall into — defaulting a rep to the PATIENT
  // dashboard). Reps get their own dedicated page instead.
  if (user.role === "sales_rep") return <Navigate to="/sales" replace />;
  return <Home />;
}

function App() {
  return (
    <UserProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Proove-parity build, Part 3 — Stripe's success_url/cancel_url
              from create_checkout_session. Confirmed real gap (downstream-
              mismatch-checker finding): these didn't exist at all, so a
              patient who actually paid via PanelCheckoutScreen got silently
              bounced to login with no confirmation either way. Deliberately
              NOT behind ProtectedRoute — not necessarily logged in yet. */}
          <Route path="/payments/success" element={<PaymentSuccess />} />
          <Route path="/payments/cancelled" element={<PaymentCancelled />} />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <RoleHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient-bot"
            element={
              <ProtectedRoute requiredRole="patient">
                <Patient />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clinician-bot"
            element={
              <ProtectedRoute requiredRole="clinician">
                <Clinician />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-reports"
            element={
              <ProtectedRoute>
                <LabReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-summaries"
            element={
              <ProtectedRoute>
                <MySummaries />
              </ProtectedRoute>
            }
          />
          {/* "My Results" ecosystem plan, Phase 1 — patient-only, same
              requiredRole convention as /my-payment/my-digest below. */}
          <Route
            path="/my-results"
            element={
              <ProtectedRoute requiredRole="patient">
                <MyResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-payment"
            element={
              <ProtectedRoute requiredRole="patient">
                <MyPayments />
              </ProtectedRoute>
            }
          />
          {/* Part 11c — the real page the "your digest is ready" email
              links to (DIGEST_PAGE_URL, backend main.py). Patient-only,
              same requiredRole convention as /my-payment above — the
              monthly digest is a patient-facing feature only. */}
          <Route
            path="/my-digest"
            element={
              <ProtectedRoute requiredRole="patient">
                <MonthlyDigest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-test"
            element={
              <ProtectedRoute requiredRole="clinician">
                <OrderTest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-patients"
            element={
              <ProtectedRoute requiredRole="clinician">
                <MyPatients />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-patients/:patientId"
            element={
              <ProtectedRoute requiredRole="clinician">
                <MyPatients />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-patients-results"
            element={
              <ProtectedRoute requiredRole="clinician">
                <MyPatientsResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-patients-summaries"
            element={
              <ProtectedRoute requiredRole="clinician">
                <MyPatientsSummaries />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requiredRole="clinician">
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute requiredRole="clinician">
                <CompleteProfile />
              </ProtectedRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />
          <Route path="/patient-consent/:orderId" element={<PatientConsent />} />
          <Route path="/terms"           element={<TermsOfUse />} />
          <Route path="/privacy"         element={<PrivacyPolicy />} />
          <Route path="/admin-login"     element={<AdminLogin />} />
          <Route
            path="/admin-dashboard"
            element={
              <AdminProtectedRoute>
                <AdminDashboard />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin-clinicians"
            element={
              <AdminProtectedRoute>
                <AdminClinicians />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin-sales-reps"
            element={
              <AdminProtectedRoute>
                <AdminSalesReps />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/sales"
            element={
              <ProtectedRoute requiredRole="sales_rep">
                <SalesRepDashboard />
              </ProtectedRoute>
            }
          />
          {/* 2026-10-14 fix — same gap fixed in labariumaireport's own
              App.js: no catch-all route existed, so a typo'd/stale deep
              link rendered a blank page (the Firebase hosting rewrite
              correctly serves index.html, but React Router then matched
              nothing). Redirect Home — PrivateRoute-equivalent gating
              further handles the unauthenticated case. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </UserProvider>
  );
}

export default App;
